import InventoZen from "@/components/icons/InventoZen.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { error, success, toast } from "@/hooks/use-toast";
import { getMainLanguage } from "@/i18n";
import {
  LoginDTO,
  loginSchema,
  NO_BACKOFFICE_ACCESS_CODE,
} from "@/lib/auth/auth-types";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpStatusCode } from "axios";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

const Login = () => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();

  const backgroundImageUrl = t("loginBackgroundImageUrl", { defaultValue: "" });

  const didSet2FAErrorRef = useRef(false);

  const form = useForm<LoginDTO>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: state?.email || "",
      password: "",
      locale: getMainLanguage(),
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (state?.twoFactorExpired && !didSet2FAErrorRef.current) {
      form.setError("root.error", {
        message: t("login.twoFactorExpired"),
      });
      didSet2FAErrorRef.current = true;
    }
  }, [state?.twoFactorExpired, form, t]);

  const handleSubmit = async (data: LoginDTO) => {
    try {
      const result = await login(data.email, data.password, data.locale);
      // login() retourne un MultiProviderLoginResult ; on utilise primaryResponse
      // pour la navigation, qui correspond au premier provider ayant répondu.
      const response = result.primaryResponse;
      if (response.status === HttpStatusCode.Ok) {
        toast(
          success({
            title: t("login.success.title"),
            description: t("login.success.description"),
            duration: 3000,
          }),
        );
        navigate("/instance");
        return;
      }
      if (response.status === HttpStatusCode.Accepted) {
        navigate("/2fa", { state: { email: data.email } });
        return;
      }
      if (response.status === HttpStatusCode.Unauthorized) {
        form.setError("root.error", {
          message: t("login.invalidCredentials"),
        });
        toast(
          error({
            title: t("login.invalidCredentials.title"),
            description: t("login.invalidCredentials.description"),
            duration: 3000,
          }),
        );
        return;
      }
      if (
        response.status === HttpStatusCode.Forbidden &&
        response.code === NO_BACKOFFICE_ACCESS_CODE
      ) {
        form.setError("root.error", {
          message: vt(
            "login.noBackofficeAccess",
            "Ce compte n'a pas accès au back-office.",
          ),
        });
        return;
      }
      form.setError("root.error", {
        message: t("login.error"),
      });
    } catch {
      form.setError("root.error", {
        message: t("login.error"),
      });
    }
  };

  return (
    <Form {...form}>
      <div className="w-full h-full justify-between items-center inline-flex">
        <div className="w-full h-screen relative">
          <div className="h-full flex items-center justify-center">
            <div className="m-auto w-[409px] h-fit p-10 bg-white rounded-lg shadow border border-slate-200 flex-col justify-start items-center gap-8 inline-flex">
              <div>
                <div className="justify-center items-center flex">
                  <InventoZen />
                </div>
                <div className="text-center text-slate-900 text-3xl font-semibold font-['Instrument Sans'] leading-loose">
                  {t("loginBoxTitle")}
                </div>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  form.handleSubmit(handleSubmit)(e);
                }}
                className="w-full grid gap-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("emailAddress")}
                        {"*"}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("password")}
                        {"*"}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} type="password" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {form.formState.errors.root?.error && (
                  <FormMessage>
                    {form.formState.errors.root.error.message}
                  </FormMessage>
                )}
                <a
                  href="forget-password"
                  className="text-[#005d8d] text-sm font-semibold font-['Raleway'] leading-normal text-start mt-6"
                >
                  {t("passwordForgotten")}
                </a>
                <Button
                  disabled={!form.formState.isValid}
                  variant="ghost"
                  className="h-12 px-4 py-3 bg-[#008bd4] rounded-lg border border-[#006eab] justify-center items-center gap-1.5 inline-flex"
                >
                  <div className="px-0.5 justify-center items-center flex">
                    <p className="text-white text-base font-semibold font-['Raleway'] leading-normal">
                      {t("connect")}
                    </p>
                  </div>
                </Button>
              </form>
            </div>
          </div>
          <div className="absolute bottom-0 w-full h-16 px-12 py-5 bg-white border-t border-slate-200 flex justify-between items-center">
            <div className="text-center text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal">
              {`${t("copyrightInventoZen")} | ${t("version")}: ${import.meta.env.VITE_CURRENT_GIT_SHA}`}
            </div>
            <div className="justify-start items-start gap-5 flex">
              <a
                href={"#"}
                className="text-[#006eab] text-sm font-bold font-['Raleway'] leading-normal"
              >
                {t("CGV")}
              </a>
              <div className="text-center text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal">
                |
              </div>
              <a
                href={"#"}
                className="text-[#006eab] text-sm font-bold font-['Raleway'] leading-normal"
              >
                {t("footerLegalNotices")}
              </a>
              <div className="text-center text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal">
                |
              </div>
              <p className="text-[#006eab] text-sm font-bold font-['Raleway'] leading-normal">
                {t("optimizeForChrome")}
              </p>
            </div>
          </div>
        </div>
        <div
          className={cn(
            "h-screen relative md:hidden lg:block transition-all duration-300",
            backgroundImageUrl ? "w-[50%]" : "w-0",
          )}
        >
          {backgroundImageUrl && (
            <img
              src={backgroundImageUrl}
              alt="login"
              decoding="async"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute bottom-0 left-0 w-full flex-col justify-center items-center inline-flex">
            <div className="self-stretch p-8 bg-white/10 border-t border-white/10 backdrop-blur-xl flex-col justify-start items-start gap-8 flex">
              <div className="h-14 flex-col justify-start items-start gap-1 flex">
                <p className="grow shrink basis-0 text-white text-xl font-semibold font-['Instrument Sans'] leading-normal text-left">
                  {t("titleOfTheWork")}
                </p>
                <p className="self-stretch text-white text-lg font-normal font-['Raleway'] leading-normal text-left">
                  {t("artistFirstPage")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default Login;
